import React, { useEffect, useRef } from "react";

/**
 * The dimmed area behind a modal, which closes it — but only on a click that
 * genuinely belongs to the backdrop.
 *
 * REPORTED: "Clicking on the pop-up box to onboard user / when clicking around
 * in it the box disappears." A half-filled onboarding form vanished mid-edit.
 *
 * WHY `onClick={onClose}` + `stopPropagation` IS NOT ENOUGH — it was already
 * doing both. A DOM `click` fires on the nearest common ancestor of where the
 * mouse went DOWN and where it came UP. Press inside a text field, drift a few
 * pixels while selecting, release over the dimmed area, and the common
 * ancestor is the backdrop: the panel never sees the event to stop it, and the
 * modal closes. Native <select> popups can do the same, because their list is
 * an OS window rather than part of the page.
 *
 * So this tracks the mouse DOWN target and only closes when both ends of the
 * gesture were on the backdrop itself. A click that started anywhere inside
 * the panel can never close it, however far it travels.
 *
 * Escape also closes, because a modal that can only be dismissed with a mouse
 * is a trap for anyone working from the keyboard.
 */
/**
 * Drop-in replacement for `onClick={onClose}` on an existing backdrop div.
 *
 * Same rule as ModalBackdrop: close only when the gesture BEGAN and ENDED on
 * the backdrop itself. Exists so the ten backdrops already in the codebase
 * can be corrected with a one-line change each, rather than rewrapping ten
 * pieces of JSX and hoping every closing tag was matched correctly.
 *
 * The "did it start here" flag lives in a data attribute on the element
 * rather than in React state: it changes mid-gesture, must not re-render,
 * and this way the helper needs no hook and works inside components that are
 * declared inline in a page.
 *
 *   <div className="fixed inset-0 …" {...backdropClose(onClose)}>
 */
export function backdropClose(onClose: () => void) {
  return {
    onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.dataset.agBackdrop = String(e.target === e.currentTarget);
    },
    onMouseUp: (e: React.MouseEvent<HTMLElement>) => {
      const started = e.currentTarget.dataset.agBackdrop === "true";
      e.currentTarget.dataset.agBackdrop = "false";
      if (started && e.target === e.currentTarget) onClose();
    },
  };
}

export default function ModalBackdrop({
  onClose,
  children,
  className = "",
}: {
  onClose: () => void;
  children: React.ReactNode;
  /** Extra classes for layout (alignment, padding). The dimming and the
   *  full-screen positioning are fixed here so every modal matches. */
  className?: string;
}) {
  // Whether the CURRENT gesture began on the backdrop. A ref, not state:
  // it changes mid-gesture and must not cause a re-render.
  const startedOnBackdrop = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className={`fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 ${className}`}
      onMouseDown={(e) => {
        startedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (startedOnBackdrop.current && e.target === e.currentTarget) onClose();
        startedOnBackdrop.current = false;
      }}
    >
      {children}
    </div>
  );
}
